// ---------- START IMPORT BLOCK ----------
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
// Contexts & Types
import { useUser } from '../Context/UserContext';
import { usePatient } from './PatientContext';
import { useDashboardPreference } from '../Context/AppUIContext';
import type { PatientDetailsResponse, HealthBackgroundResponse } from '../../Types/schema-helpers';
// ---------- END IMPORT BLOCK ----------

interface ActivePatientContextType {
  activePatient: PatientDetailsResponse | null;
  activePatientId: string | null;
  isSelfViewing: boolean;
  isLoadingActivePatient: boolean;
  loadSelectedPatient: (patientQueryOrId: string) => Promise<PatientDetailsResponse | null>;
  resetToSelf: () => void;
  updateActiveHealthBackground: (newHealthBg: HealthBackgroundResponse) => void;
}

const ActivePatientContext = createContext<ActivePatientContextType | null>(null);

export const useActivePatient = (): ActivePatientContextType => {
  const context = useContext(ActivePatientContext);
  if (!context) {
    throw new Error('useActivePatient must be used within an ActivePatientProvider');
  }
  return context;
};

export const ActivePatientProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const { fetchPatientByQuery, isLoadingPatient, updateCachedHealthBackground } = usePatient();
  const { isProDashboardActive } = useDashboardPreference();

  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [activePatientData, setActivePatientData] = useState<PatientDetailsResponse | null>(null);
  const [isFetchingSelected, setIsFetchingSelected] = useState<boolean>(false);

  // Track whether an attempt to fetch self-data has already been made
  const hasAttemptedSelfFetch = useRef<boolean>(false);

  // Self-viewing is false whenever a specific patient query/ID is explicitly loaded (not 'me' and not null)
  const isSelfViewing = !selectedPatientId || selectedPatientId === 'me';
  const activePatientId = selectedPatientId || 'me';

  const loadSelectedPatient = useCallback(
    async (patientQueryOrId: string): Promise<PatientDetailsResponse | null> => {
      if (!patientQueryOrId) return null;

      setIsFetchingSelected(true);
      try {
        const data = await fetchPatientByQuery(patientQueryOrId);
        if (data) {
          const resolvedId = data.patient?.username || patientQueryOrId;
          setSelectedPatientId(resolvedId);
          setActivePatientData(data);
        } else {
          setActivePatientData(null);
        }
        return data;
      } catch (err) {
        console.error(`Failed to load active patient:`, err);
        setActivePatientData(null);
        return null;
      } finally {
        setIsFetchingSelected(false);
      }
    },
    [fetchPatientByQuery]
  );

  const resetToSelf = useCallback(async () => {
    if (!user || isFetchingSelected) return;

    setSelectedPatientId('me');
    setIsFetchingSelected(true);
    hasAttemptedSelfFetch.current = true;

    try {
      const selfData = await fetchPatientByQuery('me');
      setActivePatientData(selfData);
    } catch (err) {
      console.error('Failed to load self patient record:', err);
      setActivePatientData(null);
    } finally {
      setIsFetchingSelected(false);
    }
  }, [user, isFetchingSelected, fetchPatientByQuery]);

  /**
   * 🎯 Direct Sync Method: Instantly updates active state AND updates PatientContext cache
   */
  const updateActiveHealthBackground = useCallback(
    (newHealthBg: HealthBackgroundResponse) => {
      // 1. Update active view state
      setActivePatientData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          health_background: newHealthBg,
        };
      });

      // 2. Sync underlying PatientContext cache
      const currentQuery = activePatientData?.patient?.username || activePatientId;
      if (currentQuery) {
        updateCachedHealthBackground(currentQuery, newHealthBg);
      }
    },
    [activePatientData, activePatientId, updateCachedHealthBackground]
  );

  // Reset attempt flag when user changes
  useEffect(() => {
    if (!user) {
      hasAttemptedSelfFetch.current = false;
      setActivePatientData(null);
      setSelectedPatientId(null);
    }
  }, [user]);

  // Hydrate self when in patient mode and no active patient loaded yet
  useEffect(() => {
    if (user && !isProDashboardActive && !activePatientData && !hasAttemptedSelfFetch.current) {
      resetToSelf();
    }
  }, [user, isProDashboardActive, activePatientData, resetToSelf]);

  const value = useMemo(
    () => ({
      activePatient: activePatientData,
      activePatientId,
      isSelfViewing,
      isLoadingActivePatient: isLoadingPatient || isFetchingSelected,
      loadSelectedPatient,
      resetToSelf,
      updateActiveHealthBackground,
    }),
    [
      activePatientData,
      activePatientId,
      isSelfViewing,
      isLoadingPatient,
      isFetchingSelected,
      loadSelectedPatient,
      resetToSelf,
      updateActiveHealthBackground,
    ]
  );

  return <ActivePatientContext.Provider value={value}>{children}</ActivePatientContext.Provider>;
};